// Creative Strategist Files tab (Task #2377) — the project's asset library.
//
// Every chat upload lands here: preview, type + purpose, what the strategist
// understood the file to be, and the user's own description of what it is for
// (edited in place, the repo's detail-page convention). An asset links back to
// the message that introduced it.

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InlineTextarea } from "@/components/inline-edit";
import {
  AlertTriangle,
  FileSpreadsheet,
  FileText,
  Loader2,
  MessageSquare,
  Sparkles,
  Trash2,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { thumbFallback, thumbUrl } from "@/lib/img-thumb";
import {
  CS_ASSET_PURPOSE_LABELS,
  csAssetsKey,
  csFormatBytes,
  csFormatDateTime,
  csIsImageAsset,
  type CreativeStrategistContextAsset,
} from "@/lib/creative-strategist";

export function CreativeStrategistFilesTab({
  projectId,
  onOpenMessage,
}: {
  projectId: string;
  onOpenMessage: (messageId: string) => void;
}) {
  const { toast } = useToast();
  const assetsKey = csAssetsKey(projectId);
  const [confirmDelete, setConfirmDelete] = useState<CreativeStrategistContextAsset | null>(null);

  const { data: assets, isLoading } = useQuery<CreativeStrategistContextAsset[]>({
    queryKey: [assetsKey],
    enabled: !!projectId,
    // Extraction and analysis finish in the background; keep the badges honest
    // while anything is still being processed.
    refetchInterval: (query) =>
      (query.state.data ?? []).some(
        (a) =>
          a.extractionStatus === "pending" ||
          a.extractionStatus === "running" ||
          a.analysisStatus === "pending" ||
          a.analysisStatus === "running",
      )
        ? 4000
        : false,
  });

  const remove = useMutation({
    mutationFn: async (assetId: string) => {
      await apiRequest("DELETE", `/api/creative-strategist/context-assets/${assetId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [assetsKey] });
      queryClient.invalidateQueries({
        queryKey: [`/api/creative-strategist/projects/${projectId}/activity`],
      });
      toast({ title: "File removed" });
    },
    onError: (e: Error) => {
      toast({ title: "Couldn't remove that file", description: e.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const rows = assets ?? [];
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-14 text-center space-y-2" data-testid="empty-files">
          <FileText className="h-6 w-6 mx-auto text-muted-foreground" />
          <div className="text-sm font-medium">No files yet</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Attach briefs, screenshots, competitor ads, research or spreadsheets in the chat — they
            become project files the strategist can work from.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2" data-testid="grid-context-assets">
        {rows.map((asset) => (
          <AssetCard
            key={asset.id}
            asset={asset}
            projectId={projectId}
            onOpenMessage={onOpenMessage}
            onDelete={() => setConfirmDelete(asset)}
          />
        ))}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this file?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.fileName} will be removed from this project and deleted from storage.
              Messages that referenced it stay in the conversation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-asset">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDelete) remove.mutate(confirmDelete.id);
                setConfirmDelete(null);
              }}
              data-testid="button-confirm-delete-asset"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function AssetCard({
  asset,
  projectId,
  onOpenMessage,
  onDelete,
}: {
  asset: CreativeStrategistContextAsset;
  projectId: string;
  onOpenMessage: (messageId: string) => void;
  onDelete: () => void;
}) {
  const assetsKey = csAssetsKey(projectId);
  const isImage = csIsImageAsset(asset);
  const processing =
    asset.extractionStatus === "pending" ||
    asset.extractionStatus === "running" ||
    asset.analysisStatus === "pending" ||
    asset.analysisStatus === "running";

  return (
    <Card data-testid={`card-asset-${asset.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <a
            href={asset.storageUrl}
            target="_blank"
            rel="noreferrer"
            className="shrink-0"
            data-testid={`link-asset-${asset.id}`}
          >
            {isImage ? (
              <img
                src={thumbUrl(asset.storageUrl, 220) ?? asset.storageUrl}
                onError={thumbFallback(asset.storageUrl)}
                alt={asset.fileName}
                className="h-20 w-20 rounded-md object-cover border"
              />
            ) : (
              <div className="h-20 w-20 rounded-md border bg-muted flex items-center justify-center">
                {asset.purpose === "data" ? (
                  <FileSpreadsheet className="h-7 w-7 text-muted-foreground" />
                ) : (
                  <FileText className="h-7 w-7 text-muted-foreground" />
                )}
              </div>
            )}
          </a>

          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-medium truncate" title={asset.fileName}>
                {asset.fileName}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={onDelete}
                data-testid={`button-delete-asset-${asset.id}`}
                aria-label={`Remove ${asset.fileName}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="secondary" data-testid={`badge-asset-purpose-${asset.id}`}>
                {CS_ASSET_PURPOSE_LABELS[asset.purpose] ?? asset.purpose}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {shortType(asset.fileType)} · {csFormatBytes(asset.fileSize)}
              </span>
            </div>
            <div className="text-[11px] text-muted-foreground">
              Added {csFormatDateTime(asset.createdAt)}
            </div>
          </div>
        </div>

        {processing ? (
          <div
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
            data-testid={`status-asset-processing-${asset.id}`}
          >
            <Loader2 className="h-3 w-3 animate-spin" />
            Reading this file…
          </div>
        ) : asset.analysisSummary ? (
          <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span data-testid={`text-asset-summary-${asset.id}`}>{asset.analysisSummary}</span>
          </div>
        ) : asset.processingError ? (
          <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span data-testid={`text-asset-error-${asset.id}`}>
              Couldn't read this file automatically — it can still be used as an attachment.
            </span>
          </div>
        ) : null}

        <div>
          <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
            What it's for
          </div>
          <InlineTextarea
            value={asset.userDescription}
            placeholder="Say what this file is for…"
            rows={2}
            maxLength={1000}
            testId={`input-asset-description-${asset.id}`}
            onSave={async (next) => {
              const res = await apiRequest(
                "PATCH",
                `/api/creative-strategist/context-assets/${asset.id}`,
                { userDescription: next },
              );
              const updated = (await res.json()) as CreativeStrategistContextAsset;
              queryClient.setQueryData<CreativeStrategistContextAsset[]>(
                [assetsKey],
                (prev) => (prev ?? []).map((a) => (a.id === updated.id ? updated : a)),
              );
            }}
          />
        </div>

        {asset.messageId && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onOpenMessage(asset.messageId!)}
            data-testid={`button-asset-open-message-${asset.id}`}
          >
            <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
            View in chat
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function shortType(fileType: string): string {
  const map: Record<string, string> = {
    "image/png": "PNG",
    "image/jpeg": "JPG",
    "image/webp": "WebP",
    "application/pdf": "PDF",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
    "text/plain": "TXT",
    "text/csv": "CSV",
  };
  return map[fileType] ?? (fileType.split("/").pop() ?? fileType).toUpperCase();
}
